import { useEffect, useMemo } from "react";

interface MetaNameItem {
  name: string;
  content: string;
}

interface MetaPropertyItem {
  property: string;
  content: string;
}

interface CmsMeta {
  title?: string;
  metaNameList?: { list?: MetaNameItem[] };
  metaPropertyList?: { list?: MetaPropertyItem[] };
}

interface CmsContentHeadInput {
  meta?: CmsMeta;
  jsonLd?: string | null;
}

const MANAGED = "data-cms-managed";

function removeManaged() {
  document.head.querySelectorAll(`[${MANAGED}]`).forEach((node) => node.remove());
}

function appendManaged(tag: string, attributes: Record<string, string>, textContent?: string) {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) {
    el.setAttribute(key, value);
  }
  if (textContent) {
    el.textContent = textContent;
  }
  el.setAttribute(MANAGED, "true");
  document.head.appendChild(el);
}

export function useCmsManagedHead(
  cmsContent: CmsContentHeadInput | null | undefined,
  canonicalUrl: string | null,
  options?: { titleOnly?: boolean },
) {
  const titleOnly = options?.titleOnly ?? false;

  useEffect(() => {
    if (cmsContent?.meta?.title) {
      document.title = cmsContent.meta.title;
    }
  }, [cmsContent?.meta?.title]);

  const metaKey = useMemo(
    () =>
      JSON.stringify({
        names: cmsContent?.meta?.metaNameList?.list ?? [],
        props: cmsContent?.meta?.metaPropertyList?.list ?? [],
      }),
    [cmsContent?.meta?.metaNameList?.list, cmsContent?.meta?.metaPropertyList?.list],
  );

  useEffect(() => {
    if (titleOnly || !cmsContent) {
      return;
    }

    removeManaged();

    if (canonicalUrl) {
      appendManaged("link", { rel: "canonical", href: canonicalUrl });
    }

    cmsContent.meta?.metaNameList?.list?.forEach((item) => {
      appendManaged("meta", { name: item.name, content: item.content });
    });

    cmsContent.meta?.metaPropertyList?.list?.forEach((item) => {
      appendManaged("meta", {
        property: item.property,
        content: item.property === "og:url" ? (canonicalUrl ?? "") : item.content,
      });
    });

    if (cmsContent.jsonLd) {
      appendManaged("script", { type: "application/ld+json" }, cmsContent.jsonLd);
    }

    return removeManaged;
  }, [canonicalUrl, metaKey, cmsContent?.jsonLd, titleOnly]);
}
